import { API_ORIGIN, XomicsApiError } from './api';

// A customer's own portal. Its people are not staff: their session is kept apart from the
// staff session, in this browser, until they sign out or it runs out (30 days).
const SESSION_KEY = 'xomics-customer-session';

export function customerSession() {
  try {
    return window.localStorage.getItem(SESSION_KEY);
  } catch (_error) {
    return null;
  }
}

function keepSession(token) {
  try {
    if (token) window.localStorage.setItem(SESSION_KEY, token);
    else window.localStorage.removeItem(SESSION_KEY);
  } catch (_error) {
    // Without storage the portal still works until the page is closed.
  }
}

async function send(path, { method = 'GET', body, raw = false } = {}) {
  const headers = { Accept: raw ? '*/*' : 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const token = customerSession();
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${API_ORIGIN}/CustomerPortal${path}`, {
    method, headers, body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (raw && response.ok) return response;
  const type = response.headers.get('content-type') || '';
  const data = type.includes('application/json') ? await response.json() : null;
  if (!response.ok) {
    if (response.status === 401) keepSession(null);
    throw new XomicsApiError(data?.error || `Request failed with status ${response.status}`,
      { status: response.status, code: data?.code || null, body: data });
  }
  return data;
}

export const readLink = (token) => send(`/links/${encodeURIComponent(token)}`);

export async function joinWithLink(token, account) {
  const body = await send(`/links/${encodeURIComponent(token)}/account`, { method: 'POST', body: account });
  keepSession(body.token);
  return body;
}

export async function signIn(email, password) {
  const body = await send('/sessions', { method: 'POST', body: { email, password } });
  keepSession(body.token);
  return body;
}

// A forgotten password: a link to choose a new one is emailed when the address has an account.
export const askPasswordReset = (email) => send('/password-resets', { method: 'POST', body: { email } });
export const readPasswordReset = (token) => send(`/password-resets/${encodeURIComponent(token)}`);

export async function choosePassword(token, password) {
  const body = await send(`/password-resets/${encodeURIComponent(token)}`, { method: 'POST', body: { password } });
  keepSession(body.token);
  return body;
}

export async function signOut() {
  try {
    await send('/sessions', { method: 'DELETE' });
  } finally {
    keepSession(null);
  }
}

export const loadMe = () => send('/me');
export const loadOverview = () => send('/overview');
// A project (its address still says submissions, as it did before projects).
export const loadSubmission = (uuid) => send(`/submissions/${encodeURIComponent(uuid)}`);
// A filled template read back for a project's next submission, then that submission sent.
// The chosen sample type says which of the form's columns its samples need.
export const checkSamples = (uuid, file, sampleTypeCode) => send(`/submissions/${encodeURIComponent(uuid)}/check`,
  { method: 'POST', body: { file, sampleTypeCode: sampleTypeCode || null } });
export const sendSamples = (uuid, samples) => send(`/submissions/${encodeURIComponent(uuid)}/samples`, { method: 'POST', body: samples });

// Patient reports on a submission's samples, asked for with the person of each (or not wanted).
const reportsPath = (uuid, number) => `/submissions/${encodeURIComponent(uuid)}/forms/${encodeURIComponent(number)}/patient-reports`;
export const askPatientReports = (uuid, number, body) => send(reportsPath(uuid, number), { method: 'POST', body });
export const declinePatientReports = (uuid, number) => send(`${reportsPath(uuid, number)}/decline`, { method: 'POST', body: {} });

// A link opened in the tab the click opened (opened first, so the browser does not block it).
async function openLink(path) {
  const opened = window.open('', '_blank');
  try {
    const { url } = await send(path);
    if (opened) opened.location.href = url;
    else window.location.assign(url);
  } catch (error) {
    if (opened) opened.close();
    throw error;
  }
}

// A patient report's PDF.
export const openPatientReport = (uuid) => openLink(`/patient-reports/${encodeURIComponent(uuid)}`);

// Their answers after our first quality check of a submission's samples: go on or stop for each, with a note to us.
const boxPath = (uuid, number) => `/submissions/${encodeURIComponent(uuid)}/forms/${encodeURIComponent(number)}`;
export const answerQc = (uuid, number, body) => send(`${boxPath(uuid, number)}/qc-answers`, { method: 'POST', body });

// Files of a submission: theirs added (each { name, base64 }) or taken away, and either side's opened.
export const addSubmissionFiles = (uuid, number, files) => send(`${boxPath(uuid, number)}/files`, { method: 'POST', body: { files } });
export const removeSubmissionFile = (uuid, number, fileUuid) => send(`${boxPath(uuid, number)}/files/${encodeURIComponent(fileUuid)}`, { method: 'DELETE' });
export const openSubmissionFile = (uuid) => openLink(`/files/${encodeURIComponent(uuid)}`);

// What our laboratory shared on a project: how much, a page of the samples with files, the files of the
// whole project or of one sample, one file, and a list of links to all of them for a download tool.
const sharedPath = (uuid) => `/submissions/${encodeURIComponent(uuid)}/shared-data`;
export const loadSharedData = (uuid, { q = '', offset = 0 } = {}) => send(`${sharedPath(uuid)}?${new URLSearchParams({ offset: String(offset), ...(q ? { q } : {}) })}`);
export const loadSharedFiles = (uuid, sample = null, offset = 0) => send(`${sharedPath(uuid)}/files?${new URLSearchParams({ offset: String(offset), ...(sample === null ? {} : { sample: String(sample) }) })}`);
export async function openSharedFile(file) {
  const { url } = await send(`/shared-files/${encodeURIComponent(file)}`);
  window.location.assign(url);
}
export async function downloadSharedList(uuid, sample = null) {
  const response = await send(`${sharedPath(uuid)}/list${sample === null ? '' : `?sample=${encodeURIComponent(sample)}`}`, { raw: true });
  const name = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') || '')?.[1] ?? 'download-links.txt';
  saveBlob(await response.blob(), name);
}

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// The template of a workflow, as .xlsx or .csv.
// The laboratory's sample submission form of a workflow; for a project, it names the project.
export async function downloadTemplate(workflow, format, projectUuid = null) {
  const query = format === 'csv' ? '?format=csv' : projectUuid ? `?project=${encodeURIComponent(projectUuid)}` : '';
  const response = await send(`/workflows/${encodeURIComponent(workflow.id)}/template${query}`, { raw: true });
  saveBlob(await response.blob(), `${workflow.code.toLowerCase()}-sample-submission-form.${format === 'csv' ? 'csv' : 'xlsx'}`);
}

// A submission's quote (by the submission) or an invoice, as a page to print, in the tab the click opened.
export async function openDocument(kind, uuid) {
  const opened = window.open('', '_blank');
  try {
    const response = await send(`/documents/${encodeURIComponent(kind)}/${encodeURIComponent(uuid)}`, { raw: true });
    const url = URL.createObjectURL(new Blob([await response.text()], { type: 'text/html' }));
    if (opened) opened.location.href = url;
    else window.location.assign(url);
  } catch (error) {
    if (opened) opened.close();
    throw error;
  }
}

// A chosen file, as the API takes it.
export function fileForUpload(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, base64: String(reader.result).split(',')[1] || '' });
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
